// Import the mock API
import { mockApi } from './mocks/mockApi';

// Import toast
import { toast } from 'react-toastify';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!BASE_URL) {
  throw new Error('REACT_APP_API_BASE_URL environment variable is not defined');
}
const IS_TEST = false;
console.log(BASE_URL,process.env);

// Helper function to get auth header
const getAuthHeader = () => ({
  'x-api-token': localStorage.getItem('adminToken') || ''
});

// Helper function to handle API responses
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const error = await response.json();
    
    // Handle different error status codes
    if (response.status >= 400 && response.status < 500) {
      // Client errors (4xx)
      toast.error(error.error || 'Invalid request. Please check your input.');
    } else if (response.status >= 500) {
      // Server errors (5xx)
      toast.error('An unexpected server error occurred. Please try again later.');
    }
    
    throw new Error(error.error || 'API request failed');
  }
  return response.json();
};

export const api = {
  // Auth APIs
  auth: {
    login: async (username: string, password: string) => {
      if (IS_TEST) {
        const mockResponse = await mockApi.auth.login(username, password);
        return handleResponse(mockResponse);
      }
      
      const response = await fetch(`${BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      return handleResponse(response);
    },
  },

  // Dashboard APIs
  dashboard: {
    getStats: async () => {
      if (IS_TEST) {
        const mockResponse = await mockApi.dashboard.getStats();
        return handleResponse(mockResponse);
      }
      
      const response = await fetch(`${BASE_URL}/api/admin/dashboard`, {
        headers: getAuthHeader(),
      });
      return handleResponse(response);
    },
  },

  // User APIs
  users: {
    getAll: async (page: number = 1, limit: number = 10, filters?: { email?: string; gender?: string }) => {
      if (IS_TEST) {
        const mockResponse = await mockApi.users.getAll(page, limit);
        return handleResponse(mockResponse);
      }
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(filters?.email && { email: filters.email }),
        ...(filters?.gender && filters.gender !== 'all' && { gender: filters.gender })
      });
      
      const response = await fetch(`${BASE_URL}/api/admin/users?${queryParams}`, {
        headers: getAuthHeader(),
      });
      return handleResponse(response);
    },

    getById: async (userId: string) => {
      if (IS_TEST) {
        const mockResponse = await mockApi.users.getById(userId);
        return handleResponse(mockResponse);
      }
      
      const response = await fetch(`${BASE_URL}/api/admin/users/${userId}`, {
        headers: getAuthHeader(),
      });
      return handleResponse(response);
    },

    exportAll: async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/admin/export`, {
          method: 'GET',
          headers: {
            ...getAuthHeader(),
          },
        });

        if (!response.ok) {
          const error = await response.json();
          toast.error(error.error || 'Failed to export users');
          throw new Error(error.error || 'Failed to export users');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `users_export_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting users:', error);
        throw error;
      }
    },

    exportById: async (userId: string) => {
      try {
        const response = await fetch(`${BASE_URL}/api/admin/export/${userId}`, {
          method: 'GET',
          headers: {
            ...getAuthHeader(),
          },
        });

        if (!response.ok) {
          const error = await response.json();
          toast.error(error.error || 'Failed to export user data');
          throw new Error(error.error || 'Failed to export user data');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `user_${userId}_export_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting user data:', error);
        throw error;
      }
    }
  },

  // Document APIs
  documents: {
    getDownloadUrl: (documentId: number): string => {
      if (IS_TEST) {
        // Return mock URL for testing
        return `${window.location.origin}/mock-documents/${documentId}`;
      }
      
      const token = localStorage.getItem('adminToken') || '';
      return `${BASE_URL}/api/admin/documents/${documentId}/download?token=${token}`;
    },
  },
}; 